type TTurnstileRenderOptions = {
  sitekey: string
  execution: 'render' | 'execute'
  appearance: 'always' | 'execute' | 'interaction-only'
  theme: 'auto' | 'light' | 'dark'
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
  'timeout-callback': () => void
  'before-interactive-callback': () => void
}

type TTurnstile = {
  render: (container: HTMLElement, options: TTurnstileRenderOptions) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TTurnstile
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
// The invisible check finishes in a few seconds; a visitor solving an
// interactive challenge needs longer (Cloudflare also times it out itself).
const TOKEN_TIMEOUT_MS = 15_000
const INTERACTIVE_TOKEN_TIMEOUT_MS = 120_000

// One widget per page, shared by every caller on the client.
let scriptPromise: Promise<TTurnstile | null> | null = null
let widgetId: string | null = null
let widgetContainer: HTMLElement | null = null
let pending: {
  resolve: (token: string | null) => void
  timer: ReturnType<typeof setTimeout>
} | null = null

const settle = (token: string | null) => {
  if (!pending) return

  clearTimeout(pending.timer)
  pending.resolve(token)
  pending = null
}

// Cloudflare wants the visitor to interact: give them time, and bring the
// widget into view, since it renders below the button they just pressed.
const onInteractive = () => {
  if (pending) {
    clearTimeout(pending.timer)
    pending.timer = setTimeout(() => settle(null), INTERACTIVE_TOKEN_TIMEOUT_MS)
  }

  widgetContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const loadTurnstile = (): Promise<TTurnstile | null> => {
  if (window.turnstile) return Promise.resolve(window.turnstile)

  scriptPromise ??= new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve(window.turnstile ?? null)
    // A blocked script must not block the form: requests go without a token
    // and the Hub decides per endpoint.
    script.onerror = () => {
      scriptPromise = null
      resolve(null)
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

/**
 * Cloudflare Turnstile for the public forms (AP-11102). `mount` renders an
 * invisible widget that only shows when Cloudflare wants an interaction;
 * `getToken` runs a fresh challenge per request, since the Hub accepts each
 * token once. Resolves to null when Turnstile is not configured or fails, so
 * callers just send no token.
 */
export const useTurnstile = () => {
  const siteKey = useRuntimeConfig().public.turnstile?.siteKey ?? ''

  const mount = async (container: HTMLElement) => {
    if (!import.meta.client || !siteKey || widgetId) return

    const turnstile = await loadTurnstile()
    // The component may have unmounted while the script loaded; an orphaned
    // widget would hold `widgetId` and block every later mount.
    if (!turnstile || widgetId || !container.isConnected) return

    widgetContainer = container
    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      execution: 'execute',
      appearance: 'interaction-only',
      theme: 'light',
      callback: (token) => settle(token),
      'error-callback': () => settle(null),
      'expired-callback': () => settle(null),
      'timeout-callback': () => settle(null),
      'before-interactive-callback': onInteractive,
    })
  }

  const unmount = () => {
    settle(null)

    if (widgetId) window.turnstile?.remove(widgetId)
    widgetId = null
    widgetContainer = null
  }

  const getToken = (): Promise<string | null> => {
    const turnstile = import.meta.client ? window.turnstile : undefined
    if (!turnstile || !widgetId) return Promise.resolve(null)

    const id = widgetId
    settle(null)

    return new Promise((resolve) => {
      pending = {
        resolve,
        timer: setTimeout(() => settle(null), TOKEN_TIMEOUT_MS),
      }
      turnstile.reset(id)
      turnstile.execute(id)
    })
  }

  return { mount, unmount, getToken }
}
